import React, { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";

import { facilityPageSelectorV2 as facilityPageSelector } from "store/selectors/FacilityPage";

import { Page, Section } from "components/core/Page";
import Container from "components/core/Page/Container";
import { H2 } from "components/core/Typography";
import { FacilityAmenitiesRanked } from "components/facility/FacilityAmenitiesRanked";
import { FacilityAmenitiesAccordion } from "components/facility/FacilityAmenitiesAccordion";
import { ScrollToTop } from "components/core/ScrollToTop";
import { FacilityPageHeader } from "components/facility/FacilityPageHeaderV2";
import { BreadcrumbsV2 } from "components/search/Breadcrumbs";
import UnitsListV2 from "components/facility/UnitsListV2/UnitsListV2";
import FacilityUnitProductSchema from "components/facility/FacilityUnitProductSchema";
import FacilityWebPageSchema from "components/facility/FacilityWebPageSchema";
import FacilitySelfStorageSchema from "components/facility/FacilitySelfStorageSchema";
import FacilityWebSiteSchema from "components/facility/FacilityWebSiteSchema";
import { FacilityGallery } from "components/organisms/facility/FacilityGallery";
import { NearbyFacilitiesUnits } from "components/facility/sections";
import { Reviews } from "components/organisms/facility/Reviews";

import "./FacilityPageV2.scss";
import { TrackingClient } from "@sparefoot/tracking-client";
import usePageViewTracking from "hooks/tracking/usePageViewTracking";
import { PAGE_TYPES } from "@sparefoot/tracking-events";
import TopBanner from "components/molecules/TopBanner/TopBanner";
import OMIIcon from "assets/icons/online-move.svg";
import Coupon from "assets/icons/coupon.svg";
import questionMarkIcon from "assets/icons/questionMarkIcon.svg";
import { HowItWorks, StorageNearYou } from "components/organisms";


function computeDiscounts(itemsObj, perRow = 3) {
	if (!itemsObj) return { discountItems: [], rows: [] };

	const discountItems = Object.entries(itemsObj)
		.filter(([, value]) => value?.hasAttribute === true)
		.map(([key, value]) => ({ key, label: value.label }));

	const rows = [];
	for (let i = 0; i < discountItems.length; i += perRow) {
		rows.push(discountItems.slice(i, i + perRow));
	}

	return { discountItems, rows };
}

/**
 * FacilityPageV2 - Variation for A/B testing
 * This is a copy of FacilityPage that can be modified independently
 * for testing experimental changes
 */
function FacilityPageV2() {
	const {
		meta,
		facilityId,
		headerProps,
		imagesProps,
		staticMapUrl,
		featuresAndAmenitiesProps,
		facilityUnits,
		nearbyFacilitiesUnits,
		hasUnits,
		location,
		promotions,
		hoursProps,
		descriptionProps,
		featuresDataByCategory,
		reviewsProps,
		facilityBannerProps,
		facilitySchemaProps,
		nearbyFacilitiesProps,
		isHidden,
	} = useSelector(facilityPageSelector);
	const baseUrl = useSelector((state) => state?.app?.baseUrl);

	// Destructure breadcrumb and header props
	const {
		location: breadcrumbLocation,
		cityUrl,
		url: facilityUrl,
		name: facilityName,
		stateUrl,
		...restHeaderProps
	} = headerProps;

	const allUnits = facilityUnits.storageUnits.concat(
		facilityUnits.vehicleUnits
	);

	const cheapestStorageUnit = facilityUnits.storageUnits.length > 0
		? facilityUnits.storageUnits.reduce(
			(cheapest, unit) => (unit.lowestPrice < cheapest.lowestPrice ? unit : cheapest)
		)
		: null;
	const currentFacilityLowestPrice = cheapestStorageUnit?.lowestPrice ?? null;
	const currentFacilityStartingPrice = cheapestStorageUnit?.startingPrice ?? null;

	usePageViewTracking(PAGE_TYPES.FACILITY);
	useEffect(() => {
		const key = `scroll_position:${window.location.pathname}${window.location.search}`;
		const savedY = sessionStorage.getItem(key);
		if (savedY !== null) {
			sessionStorage.removeItem(key);
			requestAnimationFrame(() => {
				window.scrollTo(0, parseInt(savedY, 10));
			});
		}
	}, []);
	useEffect(() => {
		TrackingClient.trackFacilityViewed({
			facility_id: facilityId,
			facility_name: headerProps.name,
			facility_promo: promotions,
			facility_rank: "" // need to check where to fetch rank
		});
	}, []);

	useEffect(() => {
		// Remove #reviews hash on page load/refresh
		if (typeof window !== 'undefined' && window.location.hash === '#reviews') {
			window.history.replaceState(
				null,
				'',
				window.location.pathname + window.location.search
			);
		}
	}, []);

	const { discountItems: availableDiscounts, rows: discountRows } =
		useMemo(() => {
			const items = facilityBannerProps.discountItems || null;
			return computeDiscounts(items, 3);
		}, [facilityBannerProps.discountItems]);

	const bannerTitle = isHidden
		? "Online reservations aren't available through Storage. Please contact the facility directly to book."
		: "Reserve Now! Pricing and availability may change.";

	return (
		<Page
			className="facility-page-v2"
			pageType={PAGE_TYPES.FACILITY}
			title={meta.title}
			description={meta.description}
			noIndex={meta.noIndex}
			canonical={meta.canonical}
			socialImageUrl={imagesProps?.images[0]}
			hideGlobalSchemas
		>
			{/* Scroll To Top */}
			{hasUnits && <ScrollToTop />}
			<Container className="page-sections">
				<BreadcrumbsV2
					location={breadcrumbLocation}
					cityUrl={cityUrl}
					facilityUrl={facilityUrl}
					facilityName={facilityName}
					stateUrl={stateUrl}
					allowTruncation
				/>
				<TopBanner
					title={bannerTitle}
					type="promo"
				/>
				<FacilityPageHeader
					name={facilityName}
					facilityUrl={facilityUrl}
					{...restHeaderProps}
					phone={isHidden ? null : restHeaderProps.phone}
				/>
				<div className="gallery-amenities-container">
					<FacilityGallery
					imagesProps={imagesProps}
					facilityName={headerProps.name}
					staticMapUrl={staticMapUrl}
					facility={headerProps}
					currentFacilityLowestPrice={currentFacilityLowestPrice}
					currentFacilityStartingPrice={currentFacilityStartingPrice}
					facilities={nearbyFacilitiesUnits}
					facilityCards={nearbyFacilitiesProps}
				/>
					<Section className="facility-highlights">
						<H2
							last
							className="facility-highlights-title"
						>
							Facility Highlights
						</H2>
						<FacilityAmenitiesRanked
							groupedAmenities={
								featuresAndAmenitiesProps.amenities
							}
							storageUnits={facilityUnits?.storageUnits || []}
							vehicleUnits={facilityUnits?.vehicleUnits || []}
						/>
					</Section>
				</div>
			</Container>
			<div className="unit-cards-section-container">
				<Container className="unit-cards-section">
					<Section className="facility-banners">
						{facilityBannerProps.hasOnlineMoveins && (
							<TopBanner
								type="omi"
								iconAlt="Online Move In Icon"
								icon={OMIIcon}
								title="Online Move-In Available"
								content="Rent online, skip the counter, and go straight to your space"
							/>
						)}
						{availableDiscounts.length > 0 &&
							discountRows.map((row) => {
								const rowKey = row
									.map((item) => item.key)
									.join("-");
								return (
									<div
										className={`discount-row discount-row--${row.length}`}
										key={rowKey}
									>
										{row.map(({ key, label }) => (
											<div
												className="discount-item"
												key={key}
											>
												<TopBanner
													type="discount"
													title={label
														.split("(")[0]
														.trim()}
													icon={Coupon}
													iconAlt="Discount Icon"
													tooltipIcon={
														questionMarkIcon
													}
													tooltipMessage={label}
												/>
											</div>
										))}
									</div>
								);
							})}
					</Section>
					{/* Nearby Facility Units */}
					{!hasUnits ? (
						<NearbyFacilitiesUnits units={nearbyFacilitiesUnits} />
					) : (
						<UnitsListV2
							units={allUnits}
							facilityId={facilityId}
							cityUrl={headerProps.cityUrl}
							facilityName={headerProps.name}
							location={location}
						/>
					)}
				</Container>
			</div>
			<Container className="page-sections">
				<FacilityAmenitiesAccordion
					title="Complete Amenities & Features"
					featuresDataByCategory={featuresDataByCategory}
					name={headerProps.name}
					description={descriptionProps.description}
					hours={hoursProps}
				/>
			</Container>
			<Reviews {...reviewsProps} />
			<HowItWorks />
			{hasUnits && (
				<StorageNearYou
					pageType="facility"
					sectionTitle="Find Storage Units Near You"
					stateTitlePrefix="Storage Units Across"
					countryTitle="Storage Units In Popular Cities"
					facilityLocation={location}
				/>
			)}
			{/* Schemas */}
			<FacilityUnitProductSchema
				units={allUnits}
				facility={facilitySchemaProps}
				baseUrl={baseUrl}
			/>
			<FacilityWebPageSchema
				facilityName={facilityName}
				canonicalUrl={meta.canonical}
				baseUrl={baseUrl}
				city={location?.city}
				stateCode={location?.state}
			/>
			<FacilitySelfStorageSchema
				facility={facilitySchemaProps}
				phone={headerProps.phone}
				baseUrl={baseUrl}
				units={facilityUnits}
				images={imagesProps?.images}
				googleReviews={reviewsProps?.googleReviews}
			/>
			<FacilityWebSiteSchema baseUrl={baseUrl} />
		</Page>
	);
}

export default FacilityPageV2;
